import { PARTS, SKILLS, partOf } from "@/data/parts";

export const tokens = (s) => (s || "").toLowerCase().replace(/[’]/g, "'").replace(/[^a-z0-9'\s-]/g, " ").split(/\s+/).filter(Boolean);
export const countWords = (s) => tokens(s).length;

export const diffWords = (ref, hyp) => {
  const a = tokens(ref), b = tokens(hyp);
  const dp = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--)
    for (let j = b.length - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const ops = [];
  let i = 0, j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { ops.push({ t: "eq", w: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) ops.push({ t: "miss", w: a[i++] });
    else ops.push({ t: "extra", w: b[j++] });
  }
  while (i < a.length) ops.push({ t: "miss", w: a[i++] });
  while (j < b.length) ops.push({ t: "extra", w: b[j++] });
  return { ops, matched: dp[0][0], refLen: a.length, hypLen: b.length };
};

const f1 = (ref, hyp) => {
  const d = diffWords(ref, hyp);
  if (!d.refLen || !d.hypLen) return { score: 0, diff: d };
  const p = d.matched / d.hypLen, r = d.matched / d.refLen;
  return { score: p + r ? (2 * p * r) / (p + r) : 0, diff: d };
};

const keywordCoverage = (keywords, text) => {
  const t = tokens(text).join(" ");
  const hits = keywords.filter((k) => tokens(k).every((w) => t.includes(w)));
  return { hits, missed: keywords.filter((k) => !hits.includes(k)), ratio: keywords.length ? hits.length / keywords.length : 0 };
};

export const typingStats = (target, typed, elapsed) => {
  const mins = Math.max(elapsed, 1) / 60;
  let correct = 0;
  for (let i = 0; i < typed.length; i++) if (typed[i] === target[i]) correct++;
  const accuracy = typed.length ? correct / typed.length : 0;
  const wpm = Math.round(typed.length / 5 / mins);
  return { wpm, accuracy, netWpm: Math.round((correct / 5) / mins) };
};

export const scoreItem = (partKey, item, resp = {}) => {
  const text = resp.text || "";
  switch (partKey) {
    case "A": case "B": case "G": { const r = f1(item.text, text); return { score: r.score, ref: item.text, diff: r.diff }; }
    case "C": { const r = f1(item.answer, text); return { score: r.score, ref: item.answer, diff: r.diff }; }
    case "D": {
      const h = ` ${tokens(text).join(" ")} `;
      const ok = item.answers.some((a) => h.includes(` ${tokens(a).join(" ")} `));
      return { score: ok ? 1 : 0, correct: ok, accepted: item.answers };
    }
    case "E": {
      const s = typingStats(item.text, text, resp.elapsed || 60);
      const r = f1(item.text.slice(0, Math.max(text.length, 1)), text);
      return { score: Math.min(s.netWpm / 45, 1) * 0.5 + s.accuracy * 0.5, ...s, ref: item.text.slice(0, text.length), diff: r.diff };
    }
    case "F": {
      const w = tokens(text);
      const ok = w.length === 1 && item.answers.map((a) => a.toLowerCase()).includes(w[0]);
      return { score: ok ? 1 : 0, correct: ok, accepted: item.answers };
    }
    case "H": {
      const k = keywordCoverage(item.keywords, text);
      const len = Math.min(countWords(text) / Math.max(countWords(item.text) * 0.6, 1), 1);
      return { score: k.ratio * 0.7 + len * 0.3, ...k, ref: item.text, diff: diffWords(item.text, text) };
    }
    case "I": {
      const sw = countWords(resp.summary), ow = countWords(resp.opinion);
      const k = keywordCoverage(item.keywords, resp.summary);
      const sumLen = sw >= 25 && sw <= 50 ? 1 : sw === 0 ? 0 : sw < 25 ? sw / 25 : Math.max(0, 1 - (sw - 50) / 50);
      const op = Math.min(ow / 50, 1);
      return { score: k.ratio * 0.35 + sumLen * 0.25 + op * 0.4, summaryWords: sw, opinionWords: ow, ...k };
    }
    default: return { score: 0 };
  }
};

export const toScaled = (pct) => Math.round(20 + 60 * Math.max(0, Math.min(1, pct)));
export const PASS_SCORE = 61;

export const buildReport = (results) => {
  const parts = {};
  PARTS.forEach((p) => {
    const rs = results.filter((r) => r.part === p.key);
    if (rs.length) parts[p.key] = rs.reduce((s, r) => s + r.result.score, 0) / rs.length;
  });
  const skills = {};
  SKILLS.forEach((sk) => {
    const keys = Object.keys(parts).filter((k) => partOf(k).skills.includes(sk));
    skills[sk] = toScaled(keys.length ? keys.reduce((s, k) => s + parts[k], 0) / keys.length : 0);
  });
  const overall = Math.round(SKILLS.reduce((s, k) => s + skills[k], 0) / SKILLS.length);
  return { parts, skills, overall, pass: overall >= PASS_SCORE };
};

export const cefr = (s) => (s >= 79 ? "C2" : s >= 69 ? "C1" : s >= 58 ? "B2" : s >= 47 ? "B1" : s >= 36 ? "A2" : "A1");
